import { Avatar } from "@chakra-ui/avatar";
import { Tooltip } from "@chakra-ui/tooltip";
import ScrollableFeed from "react-scrollable-feed";
import {
  Box,
  Text,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  IconButton,
  AlertDialog,
  AlertDialogOverlay,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogBody,
  AlertDialogFooter,
  Button,
  useDisclosure,
} from "@chakra-ui/react";
import { FaEllipsisV } from "react-icons/fa";
import { ChatState } from "../Context/ChatProvider";
import {
  isLastMessage,
  isSameSender,
  isSameSenderMargin,
  isSameUser,
} from "../config/ChatLogics";
import React from "react";

function ScrollableChat({ messages, onDelete, onlineUsers = [], activeChatUserId = null }) {
  const { user, selectedChat } = ChatState();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const cancelRef = React.useRef();

  const [messageToDelete, setMessageToDelete] = React.useState(null);
  const [deleteType, setDeleteType] = React.useState("");

  const confirmDelete = (msgId, type) => {
    setMessageToDelete(msgId);
    setDeleteType(type);
    onOpen();
  };

  const executeDelete = () => {
    if (messageToDelete && deleteType) {
      onDelete(messageToDelete, deleteType);
    }
    setMessageToDelete(null);
    setDeleteType("");
    onClose();
  };


  
  return (
    <>
      <ScrollableFeed>
        {messages &&
          messages.map((msg, i) => {
            const senderId = typeof msg.sender === "object" ? msg.sender._id : msg.sender;
            const isUser = senderId === user._id;

            let tickStatus = "";
            let tickColor = "gray.500";

            if (isUser && !selectedChat.isGroupChat) {
              const otherUser = selectedChat.users.find((u) => u._id !== user._id);
              const hasRead = msg.readBy?.includes(otherUser._id);
              const isLiveSeen = activeChatUserId === otherUser._id;
              const isOnline = onlineUsers.includes(otherUser._id);

              if (hasRead && isLiveSeen) {
                tickStatus = "✓✓";
                tickColor = "blue.400"; // Seen and in same chat
              } else if (hasRead || isOnline) {
                tickStatus = "✓✓";
                tickColor = "gray.500"; // Seen or online
              } else {
                tickStatus = "✓";
                tickColor = "gray.500"; // Only delivered
              }
            }

            return (
              <Box
                key={msg._id}
                display="flex"
                justifyContent={isUser ? "flex-end" : "flex-start"}
                alignItems="center"
                px={2}
                py={1}
              >
                {!isUser && (isSameSender(messages, msg, i, user._id) || isLastMessage(messages, i, user._id)) && (
                  <Tooltip label={msg.sender.name} placement="bottom-start" hasArrow>
                    <Avatar
                      mt="7px"
                      mr={1}
                      size="sm"
                      cursor="pointer"
                      name={msg.sender.name}
                      src={msg.sender.pic}
                    />
                  </Tooltip>
                )}

                <Box
                  bg={isUser ? "#000000" : "#ffffff"}
                  ml={isSameSenderMargin(messages, msg, i, user._id)}
                  mt={isSameUser(messages, msg, i, user._id) ? 3 : 10}
                  borderRadius="20px"
                  padding="5px 15px"
                  maxWidth="75%"
                  position="relative"
                >
                  <Text color={isUser ? "#ffff" : "#000"}>
                    {msg.content}
                  </Text>

                  <Box display="flex" justifyContent="space-between" alignItems="center" mt={1}>
                    <Text fontSize="xs" color={isUser ? "#ffff" : "#000"}>
                      {new Date(msg.createdAt).toLocaleDateString("en-GB", {
                        day: "numeric",
                        month: "short",
                      })}{" "}
                      -{" "}
                      {new Date(msg.createdAt).toLocaleTimeString([], {
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </Text>

                    {isUser && (
                      <Text fontSize="xs" ml={2} color={tickColor}>
                        {tickStatus}
                      </Text>
                    )}
                  </Box>
                </Box>

                {isUser && (
                  <Menu>
                    <MenuButton as={IconButton} icon={<FaEllipsisV />} size="xs" ml={1} />
                    <MenuList>
                      <MenuItem onClick={() => confirmDelete(msg._id, "me")}>Delete for me</MenuItem>
                      <MenuItem onClick={() => confirmDelete(msg._id, "everyone")}>Delete for everyone</MenuItem>
                    </MenuList>
                  </Menu>
                )}
              </Box>
            );
          })}
      </ScrollableFeed>

      <AlertDialog isOpen={isOpen} leastDestructiveRef={cancelRef} onClose={onClose}>
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">Confirm Deletion</AlertDialogHeader>
            <AlertDialogBody>
              Are you sure you want to delete this message {deleteType === "me" ? "for yourself" : "for everyone"}?
              {deleteType === "everyone" && (
                <Text mt={2} color="red.500">
                  This action cannot be undone and will remove the message for all participants.
                </Text>
              )}
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onClose}>Cancel</Button>
              <Button colorScheme="red" onClick={executeDelete} ml={3}>Delete</Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
}

export default ScrollableChat;
